import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  getDriverCashTrip,
  listDriverCash,
  saveDriverCashSettlement,
} from "../../api/driverCash";
import { isPlatformAdminUser, isTenantAdminUser } from "../../utils/auth";
import i18n from "../../i18n";

const formatUah = (value) => {
  const amount = Number(value || 0).toLocaleString("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return i18n.t("units.uahAmount", { amount });
};

const DriverCashReconciliationPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [remitted, setRemitted] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isPlatformAdminUser() && !isTenantAdminUser()) {
      navigate("/403", { replace: true });
    }
  }, [navigate]);

  const loadList = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setListLoading(true);
    setError("");
    try {
      const data = await listDriverCash({ dateFrom, dateTo });
      setList(data.results || []);
    } catch (e) {
      setError(t("admin.driverCashLoadError"));
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, [dateFrom, dateTo, t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openTrip = async (tripId) => {
    setSelectedTripId(tripId);
    setDetailLoading(true);
    setError("");
    try {
      const data = await getDriverCashTrip(tripId);
      setDetail(data);
      setRemitted(
        data.settlement?.remitted_uah != null
          ? String(data.settlement.remitted_uah)
          : String(data.expected_uah || "")
      );
      setNote(data.settlement?.note || "");
    } catch (e) {
      setError(t("admin.driverCashLoadError"));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTripId) return;
    setSaving(true);
    setError("");
    try {
      const data = await saveDriverCashSettlement(selectedTripId, {
        remittedUah: remitted,
        status: "closed",
        note,
      });
      setDetail(data);
      await loadList();
    } catch (e) {
      setError(t("admin.driverCashSaveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <MuiLink
        component={RouterLink}
        to="/administrator/dashboard"
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackIcon fontSize="inherit" />
        {t("header.adminPanel")}
      </MuiLink>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t("admin.driverCashTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("admin.driverCashHint")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 3 }}
      >
        <TextField
          type="date"
          size="small"
          label={t("admin.driverCashFrom")}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          size="small"
          label={t("admin.driverCashTo")}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" onClick={loadList} disabled={listLoading}>
          {t("admin.driverCashRefresh")}
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {listLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : list.length === 0 ? (
        <Typography color="text.secondary">{t("admin.driverCashEmpty")}</Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: "auto", mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.driverCashTrip")}</TableCell>
                <TableCell>{t("admin.driverCashDeparture")}</TableCell>
                <TableCell align="right">{t("admin.driverCashExpected")}</TableCell>
                <TableCell align="right">{t("admin.driverCashRemitted")}</TableCell>
                <TableCell align="right">{t("admin.driverCashBalance")}</TableCell>
                <TableCell>{t("admin.driverCashStatus")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.trip.id} hover selected={selectedTripId === row.trip.id}>
                  <TableCell>{row.trip.route_name}</TableCell>
                  <TableCell>
                    {row.trip.departure_date
                      ? dayjs(row.trip.departure_date).format("DD.MM.YYYY HH:mm")
                      : "—"}
                  </TableCell>
                  <TableCell align="right">{formatUah(row.expected_uah)}</TableCell>
                  <TableCell align="right">{formatUah(row.remitted_uah)}</TableCell>
                  <TableCell align="right">{formatUah(row.balance_uah)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        row.settlement?.status === "closed"
                          ? t("admin.driverCashStatusClosed")
                          : t("admin.driverCashStatusOpen")
                      }
                      color={row.settlement?.status === "closed" ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openTrip(row.trip.id)}>
                      {t("admin.driverCashOpenDetail")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {detailLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : detail ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {detail.trip.route_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("admin.driverCashExpected")}: {formatUah(detail.expected_uah)} ·{" "}
            {t("admin.driverCashBalance")}: {formatUah(detail.balance_uah)}
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              label={t("admin.driverCashRemitted")}
              value={remitted}
              onChange={(e) => setRemitted(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              size="small"
              label={t("admin.driverCashNote")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {t("admin.driverCashRecord")}
            </Button>
          </Stack>

          {(detail.rows || []).length === 0 ? (
            <Typography color="text.secondary">
              {t("admin.driverCashNoBookings")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.driverCashBooking")}</TableCell>
                  <TableCell>{t("admin.driverCashRoute")}</TableCell>
                  <TableCell>{t("admin.driverCashBookingStatus")}</TableCell>
                  <TableCell align="right">{t("admin.driverCashAmount")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.rows.map((row) => (
                  <TableRow key={row.booking_number}>
                    <TableCell>{row.booking_number}</TableCell>
                    <TableCell>
                      {row.from_city} → {row.to_city}
                    </TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell align="right">{formatUah(row.total_uah)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      ) : null}
    </Container>
  );
};

export default DriverCashReconciliationPage;
